import { supabase, isEdgeMode } from './config';
import { getTenantId } from '../tenant';
import { edgeApi } from './edgeApi';
import {
  saveAppDataRelational, saveAuthUsersRelational, appendAuthLogRelational,
} from './relationalDb';

// 'kv' (default) = legacy key-value erp_data store (authoritative source of truth).
// 'relational'   = ALSO mirror every write into the normalized tables.
// IMPORTANT: reads ALWAYS come from erp_data (complete + battle-tested). The
// relational tables are a write-only mirror for SQL/reporting — the app never
// reads from them, which avoids data appearing "missing" if a mirror write lags.
const RELATIONAL = (process.env.REACT_APP_DB_MODE || 'kv') === 'relational';

const ARRAY_KEYS = [
  'parties', 'items', 'accounts', 'taxes',
  'salesInvoices', 'salesReturns', 'deliveryChallans', 'salesOrders',
  'purchaseBills', 'purchaseReturns', 'purchaseOrders',
  'receipts', 'payments', 'journalEntries', 'expenses',
  'contraEntries', 'stockJournals', 'productions', 'stockIssues',
  'financialYears', 'prefixSettings', 'accountGroups', 'ledgerEntries',
  'bankReconciliations', 'tdsSections',
  'directReceipts', 'directPayments',
];

async function upsertRow(key, value) {
  const { error } = await supabase
    .from('erp_data')
    .upsert(
      { tenant_id: getTenantId(), key, value, updated_at: new Date().toISOString() },
      { onConflict: 'tenant_id,key' }
    );
  if (error) throw error;
}

async function getRow(key) {
  const { data, error } = await supabase
    .from('erp_data')
    .select('value')
    .eq('tenant_id', getTenantId())
    .eq('key', key)
    .single();
  if (error && error.code !== 'PGRST116') throw error;
  return data?.value ?? null;
}

export function isSupabaseConfigured() {
  return true;
}

// Build the app state object from a { app_*: value } map (shared by both the
// direct and edge load paths). Returns null if the tenant has no data yet.
function mapToState(map, defaults) {
  if (!map || !map['app_company']) return null;
  const state = {
    company: { ...(defaults?.company || {}), ...map['app_company'] },
    counters: map['app_counters']
      ? { ...(defaults?.counters || {}), ...map['app_counters'] }
      : defaults?.counters || {},
  };
  ARRAY_KEYS.forEach(key => {
    state[key] = map[`app_${key}`] || defaults?.[key] || [];
  });
  return state;
}

// Diff the state against prevState → the minimal set of { key, value } rows to
// write (shared by both the direct and edge save paths).
function buildAppRows(state, prevState) {
  const rows = [];
  if (!prevState || prevState.company !== state.company) {
    rows.push({ key: 'app_company', value: state.company });
  }
  if (!prevState || prevState.counters !== state.counters) {
    rows.push({ key: 'app_counters', value: state.counters });
  }
  ARRAY_KEYS.forEach(key => {
    if (!prevState || prevState[key] !== state[key]) {
      rows.push({ key: `app_${key}`, value: state[key] });
    }
  });
  return rows;
}

async function loadAppDataKv(defaults) {
  try {
    const { data: rows, error } = await supabase
      .from('erp_data')
      .select('key, value')
      .eq('tenant_id', getTenantId())
      .like('key', 'app_%');

    if (error) throw error;
    if (!rows || rows.length === 0) return null;

    const map = {};
    rows.forEach(r => { map[r.key] = r.value; });
    return mapToState(map, defaults);
  } catch (e) {
    console.error('Supabase load failed:', e);
    return null;
  }
}

async function saveAppDataKv(state, prevState) {
  const tenant_id = getTenantId();
  const STORAGE_KEY = `erp_app_data_${tenant_id}`;
  try {
    const rows = buildAppRows(state, prevState).map(r => ({
      tenant_id, key: r.key, value: r.value, updated_at: new Date().toISOString(),
    }));
    if (rows.length > 0) {
      const { error } = await supabase
        .from('erp_data')
        .upsert(rows, { onConflict: 'tenant_id,key' });
      if (error) throw error;
    }
  } catch (e) {
    console.error('Supabase save failed, falling back to localStorage:', e);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (lsErr) {
      console.error('localStorage fallback also failed:', lsErr);
    }
  }
}

// ─── Edge (Option B) load/save: everything routes through the Edge Function ──
async function loadAppDataEdge(defaults) {
  try {
    const { data: map } = await edgeApi.loadApp();
    return mapToState(map, defaults);
  } catch (e) {
    console.error('Edge load failed:', e);
    return null;
  }
}

async function saveAppDataEdge(state, prevState) {
  const rows = buildAppRows(state, prevState);
  if (rows.length === 0) return;
  await edgeApi.saveApp(rows);
}

async function loadAuthDataKv() {
  try {
    const value = await getRow('auth_users');
    return value ? { users: value } : null;
  } catch (e) {
    console.error('Supabase auth load failed:', e);
    return null;
  }
}

async function saveAuthDataKv(users) {
  try {
    await upsertRow('auth_users', users);
  } catch (e) {
    console.error('Supabase auth save failed:', e);
  }
}

async function loadAuthLogKv() {
  try {
    const value = await getRow('auth_log');
    return Array.isArray(value) ? value : [];
  } catch (e) {
    console.error('Supabase auth log load failed:', e);
    return [];
  }
}

// Append one audit entry. Read-modify-write; capped to the most recent 1000 rows.
async function appendAuthLogKv(entry) {
  try {
    const current = (await getRow('auth_log')) || [];
    const next = [...(Array.isArray(current) ? current : []), entry].slice(-1000);
    await upsertRow('auth_log', next);
  } catch (e) {
    console.error('Supabase auth log append failed:', e);
  }
}

async function fetchAuthUsersKv() {
  try {
    const value = await getRow('auth_users');
    return Array.isArray(value) ? value : null;
  } catch (e) {
    return null;
  }
}

// ─── Public API — routes to relational tables when the flag is on, and keeps
//     dual-writing to the legacy key-value store as a live backup. ────────────
export async function loadAppData(defaults) {
  if (isEdgeMode()) return loadAppDataEdge(defaults);
  // Always read from the authoritative key-value store.
  const data = await loadAppDataKv(defaults);
  // Keep the relational mirror fully in sync with the truth (fire-and-forget,
  // never blocks the app and never read back — so it can't cause data loss).
  if (RELATIONAL && data && data.company) {
    saveAppDataRelational(data, null).catch(e => console.error('Relational mirror sync failed:', e));
  }
  return data;
}

export async function saveAppData(state, prevState) {
  if (isEdgeMode()) return saveAppDataEdge(state, prevState);
  if (RELATIONAL) {
    try { await saveAppDataRelational(state, prevState); }
    catch (e) { console.error('Relational save failed:', e); }
  }
  // Always keep the legacy store up to date (backup; also the kv-mode store).
  await saveAppDataKv(state, prevState);
}

export async function clearAppData(defaultState) {
  await saveAppData(defaultState, null);
}

export async function loadAuthData() {
  if (isEdgeMode()) {
    try { const { users } = await edgeApi.loadAuth(); return users ? { users } : null; }
    catch (e) { console.error('Edge auth load failed:', e); return null; }
  }
  const data = await loadAuthDataKv();
  if (RELATIONAL && data && data.users) {
    saveAuthUsersRelational(data.users).catch(e => console.error('Auth mirror sync failed:', e));
  }
  return data;
}

export async function saveAuthData(users) {
  if (isEdgeMode()) {
    try { await edgeApi.saveAuth(users); } catch (e) { console.error('Edge auth save failed:', e); }
    return;
  }
  if (RELATIONAL) {
    try { await saveAuthUsersRelational(users); } catch (e) { console.error('Relational auth save failed:', e); }
  }
  await saveAuthDataKv(users);
}

export async function loadAuthLog() {
  if (isEdgeMode()) {
    try { const { log } = await edgeApi.loadLog(); return Array.isArray(log) ? log : []; }
    catch (e) { console.error('Edge auth log load failed:', e); return []; }
  }
  return loadAuthLogKv();
}

export async function appendAuthLog(entry) {
  if (isEdgeMode()) {
    try { await edgeApi.appendLog(entry); } catch (e) { console.error('Edge auth log append failed:', e); }
    return;
  }
  if (RELATIONAL) {
    try { await appendAuthLogRelational(entry); } catch (e) { console.error('Relational auth log append failed:', e); }
  }
  await appendAuthLogKv(entry);
}

export async function fetchAuthUsers() {
  if (isEdgeMode()) {
    try { const { users } = await edgeApi.loadAuth(); return Array.isArray(users) ? users : null; }
    catch (e) { return null; }
  }
  return fetchAuthUsersKv();
}
